import { useEffect, useState } from 'react';
import { createSpace, deleteSpace, renameSpace } from '../api/api';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  MessageSquare,
  FileText,
  MoreVertical,
  Search,
  Plus,
  FolderOpen,
  ArrowLeft,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { addChatToSpace } from '../api/api';
import { fetchChatSessions } from '../redux/slices/chatSessionsSlice';
import { useDispatch } from 'react-redux';
import { fetchSpaces } from '../redux/slices/spaceSlice';

export default function SpacesView({ isDarkMode }) {
  const user = useSelector((state) => state.auth.user);
  const uid = user?.id;
  const { list: spaces, loading } = useSelector((state) => state.spaces);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState('');
  const [newSpaceDescription, setNewSpaceDescription] = useState('');
  const [editingSpace, setEditingSpace] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (uid) {
      dispatch(fetchSpaces());
    }
  }, [dispatch, uid]);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpenId(null);
    if (menuOpenId) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpenId]);
  const handleCreateSpace = async () => {
    if (!newSpaceName.trim()) {
      toast.error('Space name cannot be empty 🚫');
      return;
    }

    try {
      setCreating(true);
      await createSpace(uid, newSpaceName.trim(), newSpaceDescription.trim());

      toast.success('Space created successfully 🎉');
      dispatch(fetchSpaces());

      setNewSpaceName('');
      setNewSpaceDescription('');
      setShowModal(false);
    } catch (err) {
      console.error('Failed to create space:', err);
      toast.error(err?.response?.data?.detail || 'Failed to create space ❌');
    } finally {
      setCreating(false);
    }
  };
  const handleDropToSpace = async (chatId, space) => {
    if (space.chat_session_ids?.includes(chatId)) {
      toast('This chat is already in this space.', {
        icon: 'ℹ️',
        duration: 3000,
      });
      return;
    }

    try {
      const res = await addChatToSpace(space.space_id, chatId, uid);

      toast.success(
        <span
          title={space.name}
          className="inline-block max-w-[240px] truncate align-middle"
        >
          Chat added to "{formatName(space.name)}"
        </span>
      );

      dispatch(fetchSpaces());
      dispatch(fetchChatSessions());
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to add chat to space'
      );
    }
  };
  const openRenameModal = (space) => {
    setEditingSpace(space);
    setEditName(space.name);
    setEditDescription(space.description || '');
  };
  const closeRenameModal = () => {
    setEditingSpace(null);
    setEditName('');
    setEditDescription('');
    setRenaming(false);
  };

  const handleRenameSpace = async () => {
    if (!editName.trim()) {
      toast.error('Space name cannot be empty');
      return;
    }

    const originalName = editingSpace.name || '';
    const originalDescription = editingSpace.description || '';

    const nextName = editName.trim();
    const nextDescription = editDescription.trim();

    // 🧠 HARD GUARD — NOTHING CHANGED
    if (nextName === originalName && nextDescription === originalDescription) {
      closeRenameModal();
      return;
    }

    try {
      setRenaming(true);

      const res = await renameSpace(
        editingSpace.space_id,
        uid,
        nextName,
        nextDescription
      );

      toast.success(
        <span title={nextName}>Space renamed to "{formatName(nextName)}"</span>
      );
      dispatch(fetchSpaces());
      dispatch(fetchChatSessions());
      closeRenameModal();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || 'Rename failed'
      );
    } finally {
      setRenaming(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewSpaceName('');
    setNewSpaceDescription('');
    setCreating(false);
  };
  const handleDeleteSpace = async (spaceId) => {
    try {
      await deleteSpace(spaceId, uid);
      toast.success('Space deleted 🗑️');

      dispatch(fetchSpaces());
      dispatch(fetchChatSessions());
    } catch (err) {
      console.error('Failed to delete space:', err);
      toast.error('Failed to delete ❌');
    } finally {
      setMenuOpenId(null);
    }
  };

  const filteredSpaces = spaces.filter((space) =>
    space.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const isRenameUnchanged =
    editingSpace &&
    editName.trim() === (editingSpace.name || '') &&
    editDescription.trim() === (editingSpace.description || '');
  const formatName = (name, maxLength = 32) => {
    if (!name || name.length <= maxLength) return name;
    const keep = Math.floor((maxLength - 3) / 2);
    return `${name.slice(0, keep)}...${name.slice(-keep)}`;
  };

  return (
    <div
      className={`p-6 sm:p-8 w-full h-full overflow-y-auto transition-colors ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <div className="mb-2">
            <button
              onClick={() => navigate('/app/chat')}
              className="group inline-flex items-center gap-2 px-3 py-2 rounded-md border transition-all duration-200 shadow-sm
               bg-brand text-white border-transparent hover:opacity-90"
            >
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            </button>
          </div>

          <h1
            className={`text-xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}
          >
            Spaces
          </h1>
          <p
            className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-800'
            }`}
          >
            Organize your legal documents and discussions.
          </p>
        </div>
        {spaces.length > 0 && (
          <div className="relative w-full sm:w-64">
            <Search
              className={`absolute top-3 left-3 h-4 w-4 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}
            />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-9 pr-3 py-2 w-full rounded-md border text-sm focus:outline-none focus:ring-2 ${
                isDarkMode
                  ? 'bg-gray-800 text-white border-gray-700 focus:ring-indigo-500 placeholder-gray-400'
                  : 'bg-white text-gray-900 border-gray-300 focus:ring-indigo-500 placeholder-gray-400'
              }`}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowModal(true)}
          className={`border-2 border-dashed rounded-lg flex flex-col items-center justify-center p-6 text-center transition cursor-pointer hover:border-indigo-500 ${
            isDarkMode
              ? 'border-gray-600 bg-gray-800 text-gray-300'
              : 'border-gray-300 bg-white text-gray-700'
          }`}
        >
          <div className="p-2 rounded-full bg-brandLighter w-fit flex items-center justify-center">
            <Plus className="h-6 w-6 text-brand" />
          </div>
          <h3 className="text-md mt-2 font-medium mb-1">Create New Space</h3>
          <p className="text-sm text-gray-500">
            Add relevant discussions and <br></br>documents to one space.
          </p>
        </motion.div>

        {loading ? (
          Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={idx}
              className={`border rounded-lg p-5 shadow-sm flex flex-col justify-between animate-pulse ${
                isDarkMode
                  ? 'bg-gray-800 border-gray-600 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-2">
                  <div className="h-7 w-7 rounded bg-gray-300 dark:bg-gray-700" />
                  <div className="h-4 w-24 rounded bg-gray-300 dark:bg-gray-700" />
                </div>
                <div className="h-4 w-4 rounded bg-gray-300 dark:bg-gray-700" />
              </div>

              <div className="flex items-center space-x-14 mb-4">
                <div className="h-3 w-20 rounded bg-gray-300 dark:bg-gray-700" />
                <div className="h-3 w-20 rounded bg-gray-300 dark:bg-gray-700" />
              </div>

              <div className="flex items-center justify-between mt-4 space-x-3">
                <div className="h-8 w-[70%] rounded-xl bg-gray-300 dark:bg-gray-700" />
                <div className="h-8 w-[20%] rounded-xl bg-gray-300 dark:bg-gray-700" />
              </div>
            </div>
          ))
        ) : (
          <AnimatePresence>
            {filteredSpaces.map((space, idx) => (
              <motion.div
                key={space.space_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                onDragEnter={() => setDragOverId(space.space_id)}
                onDragLeave={() => setDragOverId(null)}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverId !== space.space_id)
                    setDragOverId(space.space_id);
                }}
                onDragEnd={() => setDragOverId(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  const chatId = e.dataTransfer.getData('chatId');
                  setDragOverId(null);
                  if (chatId) {
                    handleDropToSpace(chatId, space);
                  }
                }}
                className={`border rounded-lg p-5 shadow-sm flex flex-col justify-between transition-colors cursor-pointer
    ${
      isDarkMode
        ? 'bg-gray-800 border-gray-600 text-white hover:bg-gray-700'
        : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
    }
    ${
      dragOverId === space.space_id
        ? 'ring-2 ring-indigo-500 ring-offset-1'
        : ''
    }
  `}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-2">
                    <FolderOpen className="text-brand bg-brandLighter p-1 h-7 w-7" />
                    <h3 className="font-medium text-sm">{space.name}</h3>
                  </div>
                  <div className="relative">
                    <MoreVertical
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(
                          menuOpenId === space.space_id ? null : space.space_id
                        );
                      }}
                      className={`h-4 w-4 cursor-pointer ${
                        isDarkMode
                          ? 'text-gray-400 hover:text-gray-300'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    />

                    {menuOpenId === space.space_id && (
                      <div
                        className={`absolute right-0 mt-2 w-32 rounded-md shadow-lg z-20 ${
                          isDarkMode ? 'bg-gray-700' : 'bg-white'
                        }`}
                      >
                        <button
                          onClick={() => {
                            openRenameModal(space);
                            setMenuOpenId(null);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            isDarkMode
                              ? 'text-gray-200 hover:bg-gray-600'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Rename
                        </button>

                        <button
                          onClick={() => {
                            setConfirmDeleteId(space.space_id);
                            setMenuOpenId(null);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm rounded-md ${
                            isDarkMode
                              ? 'text-red-400 hover:bg-gray-600'
                              : 'text-red-600 hover:bg-gray-100'
                          }`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`flex items-center justify-start space-x-14 mb-4 text-sm ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4" />
                    {space?.chat_count || 0} discussions
                  </div>
                  <div className="flex items-center gap-1 ">
                    <FileText className="h-4 w-4" />
                    {space.contract_count || 0} docs
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() =>
                      navigate(`/app/spaces/${space.space_id}`, {
                        state: { space },
                      })
                    }
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition shadow-sm w-full text-center ${
                      isDarkMode
                        ? 'bg-gray-700 text-brand hover:bg-gray-600'
                        : 'bg-[#F5F7FF] text-brand hover:bg-[#EBEEFC]'
                    }`}
                  >
                    Open Space
                  </button>

                  <button
                    title="Copy share link"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/app/spaces/${space.space_id}`;

                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success('Link copied to clipboard 🔗');
                      } catch {
                        const textArea = document.createElement('textarea');
                        textArea.value = url;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);

                        toast.success('Link copied (fallback) 🔗');
                      }
                    }}
                    className={`ml-3 text-sm px-3 py-2 rounded-xl font-medium transition hover:-translate-y-0.5 ${
                      isDarkMode
                        ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    Share
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={`rounded-2xl shadow-2xl p-6 w-full max-w-md transform transition-all ${
                isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">Create a New Space</h2>
                <button
                  onClick={handleCloseModal}
                  className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <input
                type="text"
                value={newSpaceName}
                onChange={(e) => setNewSpaceName(e.target.value)}
                placeholder="Enter space name"
                className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 mb-4 text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-brand'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-brand'
                }`}
              />
              <textarea
                value={newSpaceDescription}
                onChange={(e) => setNewSpaceDescription(e.target.value)}
                placeholder="Enter space description"
                rows={3}
                className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 mb-4 text-sm ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-brand'
                    : 'bg-white border-gray-300 text-gray-900 focus:ring-brand'
                }`}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseModal}
                  disabled={creating}
                  className={`px-4 py-2 rounded-lg text-sm transition ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSpace}
                  disabled={creating}
                  className="px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-brandDark to-brand text-white font-medium shadow hover:opacity-90 transition"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {confirmDeleteId && (
          <motion.div
            key="confirm-delete"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={`rounded-2xl shadow-2xl p-6 w-full max-w-md ${
                isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}
            >
              <h2 className="text-lg font-bold mb-4">Delete Space?</h2>
              <p className="mb-6 text-sm">
                Are you sure you want to delete this space? This action cannot
                be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className={`px-4 py-2 rounded-lg text-sm ${
                    isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleDeleteSpace(confirmDeleteId);
                    setConfirmDeleteId(null);
                  }}
                  className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {editingSpace && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-black/50 z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className={`p-6 rounded-2xl w-full max-w-md ${
                isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
              }`}
            >
              <h2 className="text-lg font-bold mb-4">Rename Space</h2>

              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full mb-3 px-4 py-2 rounded-lg border"
                placeholder="Space name"
              />

              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="w-full mb-4 px-4 py-2 rounded-lg border"
                placeholder="Description (optional)"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={closeRenameModal}
                  className="px-4 py-2 rounded-lg bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRenameSpace}
                  disabled={renaming || isRenameUnchanged}
                  className={`px-4 py-2 rounded-lg text-white transition ${
                    renaming || isRenameUnchanged
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-brand hover:opacity-90'
                  }`}
                >
                  {renaming ? 'Saving...' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
